// src/components/settings/shared/ShippingSection.jsx
import React from 'react';
import { Truck, MapPin, Tag, Plus, Trash2, Package } from 'lucide-react';

export const ShippingSection = ({ formData, onInputChange }) => {
  
  // Helper function to get nested shipping value
  const getShippingValue = (field) => {
    return formData?.shipping?.[field] ?? '';
  };

  // Helper function to set nested shipping value - USING DOT NOTATION
  const setShippingValue = (field, value) => {
    onInputChange(`shipping.${field}`, value);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Truck className="h-5 w-5 text-blue-600" />
          Shipping & Delivery Configuration
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure delivery options, shipping rates, and delivery zones.
        </p>
      </div>

      {/* Delivery Status */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-md font-semibold text-blue-900">Enable Deliveries</h4>
            <p className="text-sm text-blue-700">Turn on delivery services for your store</p>
          </div>
          <input
            type="checkbox"
            checked={getShippingValue('enabled') || false}
            onChange={(e) => setShippingValue('enabled', e.target.checked)}
            className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>
      </div>

      {getShippingValue('enabled') && (
        <>
          {/* Delivery Zones */}
          <div className="bg-green-50 p-6 rounded-lg mb-6">
            <h4 className="text-md font-semibold text-green-900 mb-4 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Delivery Zones
            </h4>
            
            <div className="space-y-4">
              {(getShippingValue('deliveryZones') || []).map((zone, index) => (
                <div key={index} className="bg-white p-4 rounded-lg border">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Zone Name
                      </label>
                      <input
                        type="text"
                        value={zone.name || ''}
                        onChange={(e) => {
                          const newZones = [...(getShippingValue('deliveryZones') || [])];
                          newZones[index] = { ...zone, name: e.target.value };
                          setShippingValue('deliveryZones', newZones);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Central Durban"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Delivery Fee (R)
                      </label>
                      <input
                        type="number"
                        value={zone.fee || ''}
                        onChange={(e) => {
                          const newZones = [...(getShippingValue('deliveryZones') || [])];
                          newZones[index] = { ...zone, fee: parseFloat(e.target.value) || 0 };
                          setShippingValue('deliveryZones', newZones);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="50"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Delivery Time
                      </label>
                      <input
                        type="text"
                        value={zone.deliveryTime || ''}
                        onChange={(e) => {
                          const newZones = [...(getShippingValue('deliveryZones') || [])];
                          newZones[index] = { ...zone, deliveryTime: e.target.value };
                          setShippingValue('deliveryZones', newZones);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="1-2 business days"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Coverage Areas
                    </label>
                    <input
                      type="text"
                      value={zone.areas || ''}
                      onChange={(e) => {
                        const newZones = [...(getShippingValue('deliveryZones') || [])];
                        newZones[index] = { ...zone, areas: e.target.value };
                        setShippingValue('deliveryZones', newZones);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Durban, Umhlanga, Ballito (comma separated)"
                    />
                  </div>
                  
                  <button
                    onClick={() => {
                      const newZones = (getShippingValue('deliveryZones') || []).filter((_, i) => i !== index);
                      setShippingValue('deliveryZones', newZones);
                    }}
                    className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center gap-1"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove Zone
                  </button>
                </div>
              ))}
              
              <button
                onClick={() => {
                  const newZones = [...(getShippingValue('deliveryZones') || []), {
                    name: '',
                    fee: 0,
                    deliveryTime: '',
                    areas: ''
                  }];
                  setShippingValue('deliveryZones', newZones);
                }}
                className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Delivery Zone
              </button>
            </div>
          </div>

          {/* Free Shipping */}
          <div className="bg-yellow-50 p-6 rounded-lg mb-6">
            <h4 className="text-md font-semibold text-yellow-900 mb-4 flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Free Shipping Settings
            </h4>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <input
                  type="checkbox"
                  checked={getShippingValue('freeShippingEnabled') || false}
                  onChange={(e) => setShippingValue('freeShippingEnabled', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="font-medium text-gray-900">
                  Enable Free Shipping
                </label>
              </div>
              
              {getShippingValue('freeShippingEnabled') && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Minimum Order Value (R)
                    </label>
                    <input
                      type="number"
                      value={getShippingValue('freeShippingThreshold') || ''}
                      onChange={(e) => setShippingValue('freeShippingThreshold', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="500"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Free Shipping Message
                    </label>
                    <input
                      type="text"
                      value={getShippingValue('freeShippingMessage') || ''}
                      onChange={(e) => setShippingValue('freeShippingMessage', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Free shipping on orders over R500!"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Shipping Options */}
          <div className="bg-purple-50 p-6 rounded-lg">
            <h4 className="text-md font-semibold text-purple-900 mb-4 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Additional Shipping Options
            </h4>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                <div>
                  <label className="font-medium text-gray-900">Allow Local Pickup</label>
                  <p className="text-sm text-gray-600">Customers can collect orders in person</p>
                </div>
                <input
                  type="checkbox"
                  checked={getShippingValue('allowLocalPickup') || false}
                  onChange={(e) => setShippingValue('allowLocalPickup', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                <div>
                  <label className="font-medium text-gray-900">Require Shipping Address</label>
                  <p className="text-sm text-gray-600">Collect full address at checkout</p>
                </div>
                <input
                  type="checkbox"
                  checked={getShippingValue('requireAddress') !== false}
                  onChange={(e) => setShippingValue('requireAddress', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>

              <div className="p-3 bg-white rounded-lg border">
                <label className="block font-medium text-gray-900 mb-2">
                  Handling Fee (R)
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  Additional handling fee added to all orders
                </p>
                <input
                  type="number"
                  value={getShippingValue('handlingFee') || 0}
                  onChange={(e) => setShippingValue('handlingFee', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="p-3 bg-white rounded-lg border">
                <label className="block font-medium text-gray-900 mb-2">
                  Estimated Processing Time
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  Time needed to prepare orders before shipping
                </p>
                <input
                  type="text"
                  value={getShippingValue('processingTime') || ''}
                  onChange={(e) => setShippingValue('processingTime', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="1-2 business days"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ShippingSection;